import { MediaPlayer } from "./MediaPlayer";
import { PlayBackCircle } from "./PlayBackCircle";
import { hexToRgb, getHsp } from "./utils";

class Buttons {
    constructor(frame, loopDuration) {
        this.frame = frame;
        this.currentInstrumentIndex = 0;
        this.mediaPlayer = new MediaPlayer();
        this.playBackCircle = new PlayBackCircle(loopDuration);
        this.handlers = {
            initSound: this.initSound.bind(this),
            onInstrumentChange: this.onInstrumentChange.bind(this),
        };
        this.color = null;
        this.complementaryColor = null;
        this.instrumentButtons = document.querySelectorAll("button.instrument");
        this.instrumentsContainer = document.getElementById("instrument-buttons");
        this.canvasButtonsContainer = document.getElementById("canvas-buttons");
        this.stampsDivs = document.querySelectorAll(".stamps");
        this.gridButton = document.getElementById("gridbtn");
        this.tempoButton = document.getElementById("tempobtn");
        this.clearButton = document.getElementById("clearbtn");
        this.undoButton = document.getElementById("undobtn");
        this.playButton = document.getElementById("playbtn");
        this.shareButton = document.getElementById("sharebtn");
        this.aboutButton = document.getElementById("aboutbtn");
        this.atmospheresButton = document.getElementById("atmospheres-btn");
        this.separator = document.getElementById("separator");
        this.createMyOwnButton = document.getElementById("createmyownbtn");
        this.playContainer = document.getElementById("play-container");

        this.isGridActivated = false;
        this.isTempoActivated = false;

        document.addEventListener("click", this.handlers.initSound, true);

        // Change the color of the stamps
        for (const button of this.instrumentButtons) {
            if (button.dataset.type == "stamp") {
                const color = button.dataset.color;
                this.colorPath(button, color);
            }
        }
    }

    shapesChanged(shapes) {
        this.playBackCircle.shapesChanged(shapes);
    }

    initSound() {
        document.removeEventListener("click", this.handlers.initSound, true);
    }

    show() {
        document.getElementById("footer").classList.add("shown");
    }

    clear() {
        this.playBackCircle.reset();
    }

    update(loopProgress) {
        this.playBackCircle.setProgress(loopProgress);
    }

    resize() {
        this.playBackCircle.resize();
    }

    init() {
        this.instrumentButtons.forEach((button) => {
            button.addEventListener("click", this.handlers.onInstrumentChange);
        });
        this.clearButton.addEventListener("click", () => {
            this.playDiscardSound();
            this.sendEvent("clear");
        });
        this.undoButton.addEventListener("click", () => {
            this.playDiscardSound();
            this.sendEvent("undo");
        });
        this.gridButton.addEventListener("click", () => {
            this.playDiscardSound();
            this.sendEvent("toggleGrid");
        });
        this.tempoButton.addEventListener("click", () => {
            this.playDiscardSound();
            this.sendEvent("toggleTempo");
        });
        this.playButton.addEventListener("click", () => {
            this.sendEvent("togglePlayPause");
            this.playContainer.classList.add('played');
            this.createMyOwnButton.classList.add('shown');
        });
        this.createMyOwnButton.addEventListener("click", () => {
            this.sendEvent("createMyOwn");
        });
    }

    playDiscardSound() {
        this.mediaPlayer.playDiscard();
    }

    onInstrumentChange(e) {
        this.currentInstrumentIndex = parseInt(e.currentTarget.dataset.index, 10);
        this.mediaPlayer.playSelectInstrument(this.currentInstrumentIndex);
        this.sendEvent("instrumentChanged");
    }

    changeAtmosphere(atmosphereData) {
        // display stamps
        for (const stampsDiv of this.stampsDivs) {
            if (stampsDiv.dataset.atmosphere === atmosphereData.id) {
                stampsDiv.classList.add("active");
            } else {
                stampsDiv.classList.remove("active");
            }
        }
        // Change the color of the instruments
        let i = 0;
        for (const color of atmosphereData.instrumentColors) {
            this.instrumentButtons[i].dataset.color = color;
            this.colorPath(this.instrumentButtons[i], color);
            i++;
        }
    }

    colorPath(element, color) {
        const paths = element.querySelectorAll("path");
        for (const path of paths) {
            path.style.fill = color;
        }
    }

    getInstrumentProperties(index) {
        const btn = this.getInstrumentButtonByIndex(index);
        if (btn) {
            const type = btn.dataset.type;
            return {
                color: btn.dataset.color,
                type: type,
                id: btn.dataset.id,
                isStamp: type === "stamp" ? true : false,
            };
        } else {
            return {
                color: "#ffffff",
                type: "",
                id: "",
                isStamp: false,
            };
        }
    }

    getInstrumentId(index) {
        const btn = this.getInstrumentButtonByIndex(index);
        if (btn) {
            return btn.dataset.id;
        } else {
            return '';
        }
    }

    getInstrumentButtonByIndex(index) {
        for (const btn of this.instrumentButtons) {
            const buttonIndex = parseInt(btn.dataset.index, 10);
            if (buttonIndex === index) {
                return btn;
            }
        }
        return null;
    }

    changeGridState(isOpened) {
        this.isGridActivated = isOpened;
        this.changeButtonState(this.gridButton, this.isGridActivated);
    }

    changeTempoState(isActive) {
        this.isTempoActivated = isActive;
        this.changeButtonState(this.tempoButton, this.isTempoActivated);
    }

    changeButtonState(btn, isActive) {
        if (this.complementaryColor) {
            const complementaryColorRgb = hexToRgb(this.complementaryColor);
            const complementaryColorRgbValues = `${complementaryColorRgb[0]}, ${complementaryColorRgb[1]}, ${complementaryColorRgb[2]}`;
            btn.style.backgroundColor = `rgba(${complementaryColorRgbValues}, ${isActive ? 1 : 0
                })`;
            this.colorPath(
                btn,
                isActive ? this.color : this.complementaryColor
            );
        }
    }

    selectInstrument(index, color, complementaryColor) {
        this.color = color;
        this.complementaryColor = complementaryColor;
        this.playBackCircle.changeColor(color, complementaryColor);
        const complementaryColorRgb = hexToRgb(complementaryColor);
        const colorRgb = hexToRgb(color);
        const complementaryColorRgbValues = `${complementaryColorRgb[0]}, ${complementaryColorRgb[1]}, ${complementaryColorRgb[2]}`;
        this.currentInstrumentIndex = index;
        this.instrumentButtons.forEach((button) => {
            const buttonIndex = parseInt(button.dataset.index, 10);
            if (buttonIndex === index) {
                button.style.backgroundColor = complementaryColor;
                button.classList.add("active");
            } else {
                button.style.backgroundColor = `rgba(${complementaryColorRgbValues}, 0)`;
                button.classList.remove("active");
            }
            this.colorPath(button, button.dataset.color);
        });
        this.separator.style.backgroundColor = `rgba(${complementaryColorRgbValues}, 0.25)`;
        this.changeButtonState(this.tempoButton, this.isTempoActivated);
        this.changeButtonState(this.gridButton, this.isGridActivated);

        this.colorPath(this.undoButton, complementaryColor);
        this.colorPath(this.clearButton, complementaryColor);
        this.colorPath(this.shareButton, complementaryColor);
        this.colorPath(this.atmospheresButton, complementaryColor);
        this.colorPath(this.aboutButton, complementaryColor);

        const boxShadow = `0px 2px 6px rgba(${complementaryColorRgbValues}, 0.12)`;
        this.instrumentsContainer.style.backgroundColor = color;
        this.instrumentsContainer.style.boxShadow = boxShadow;
        this.canvasButtonsContainer.style.backgroundColor = color;
        this.canvasButtonsContainer.style.boxShadow = boxShadow;

        this.shareButton.style.backgroundColor = color;
        this.shareButton.style.boxShadow = boxShadow;

        this.aboutButton.style.backgroundColor = color;
        this.aboutButton.style.boxShadow = boxShadow;



        // check button luminosity
        const colorHsp = getHsp(colorRgb[0], colorRgb[1], colorRgb[2]);
        for (const button of this.instrumentButtons) {
            const btnRgbColor = hexToRgb(button.dataset.color);
            const hsp = getHsp(btnRgbColor[0], btnRgbColor[1], btnRgbColor[2]);
            const diff = Math.abs(hsp - colorHsp);
            if (diff < 30 && !button.classList.contains("active")) {
                this.colorPath(button, complementaryColor);
            }
        }
    }

    sendEvent(eventName) {
        const event = document.createEvent("Event");
        event.initEvent(eventName, true, true);
        document.dispatchEvent(event);
    }
}

export { Buttons }